import {
  DEFAULT_FONT_SIZE,
  DEFAULT_ICON_SIZE,
  DEFAULT_LABEL_COLOR,
  STRUCTURAL_TYPES,
  type Coordinate,
  type DeskFeature,
  type DeskShapeFeature,
  type DeskType,
  type EditableFloorPlan,
  type IconLabelFeature,
  type LayerName,
  type LineBackdropFeature,
  type MultiPolygonCoordinates,
  type PolygonFeature,
  type PolygonLayer,
  type StructuralType,
  type TextLabelFeature,
} from '../models/editor-feature';

/**
 * Turns the five stored GeoJSON layers into the editor's model.
 *
 * Not one floorplan in the database carries a phase-2 property — every feature has exactly
 * `id` and `featureId` and nothing else — so every default lives here rather than being
 * scattered through the components that read them. Annotations are the same story: they
 * were written before `kind`/`text` existed and carry their content under `label`.
 *
 * Anything unrecognised is carried through untouched. A property this editor has never
 * heard of belongs to some other client, and a round-trip through here must not be what
 * deletes it.
 */

const DESK_ELEMENT_TYPE = 'desk';

const KNOWN_DESK_PROPERTIES = [
  'id', 'featureId', 'rotation', 'deskType', 'podId', 'capacity', 'roomId', 'iconName', 'iconScale',
];
const KNOWN_DESK_SHAPE_PROPERTIES = ['id', 'featureId', 'elementType', 'rotation', 'iconName'];
const KNOWN_LABEL_PROPERTIES = [
  'id',
  'featureId',
  'kind',
  'text',
  'label',
  'iconName',
  'fontSize',
  'iconSize',
  'rotation',
  'color',
];

/** A feature as it arrives from the API: loosely typed on purpose, since nothing validates it client-side. */
interface RawFeature {
  readonly type?: string;
  readonly geometry?: { readonly type?: string; readonly coordinates?: unknown };
  readonly properties?: Record<string, unknown>;
}

interface RawLayer {
  readonly type?: string;
  readonly name?: string;
  readonly features?: RawFeature[];
  readonly [key: string]: unknown;
}

export interface StoredFloorPlan {
  readonly id?: string;
  readonly officeId?: string;
  readonly storey?: number;
  /** When the server last wrote this plan. Sent back on save so a concurrent edit can be caught. */
  readonly lastUpdatedAt?: string;
  readonly publishedAt?: string | null;
  /** Set once somebody has measured the plan against a known dimension. */
  readonly unitsPerMetre?: number | null;
  readonly borderLayer?: unknown;
  readonly floorLayer?: unknown;
  readonly roomLayer?: unknown;
  readonly workplacesLayer?: unknown;
  readonly labelLayer?: unknown;
}

const asLayer = (layer: unknown): RawLayer => (typeof layer === 'object' && layer !== null ? (layer as RawLayer) : {});

const featuresOf = (layer: unknown): RawFeature[] => {
  const { features } = asLayer(layer);
  return Array.isArray(features) ? features : [];
};

/** The layer's own top-level keys minus its features, so a save can rebuild it as it was found. */
const envelopeOf = (layer: unknown): Record<string, unknown> => Object.fromEntries(
  Object.entries(asLayer(layer)).filter(([key]) => key !== 'features'),
);

const asString = (value: unknown, fallback: string): string => (typeof value === 'string' ? value : fallback);

const asNumber = (value: unknown, fallback: number): number => (
  typeof value === 'number' && Number.isFinite(value) ? value : fallback
);

/**
 * The identifier the stored data uses for "no identifier". Not a placeholder the editor
 * invented: 1,411 floor features across 34 stored floorplans carry it.
 */
const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';

const isUsableIdentifier = (value: unknown): value is string => (
  typeof value === 'string' && value !== '' && value !== EMPTY_UUID
);

/**
 * Hands out an editor identity per feature, guaranteed unique within its kind.
 *
 * Selection, hover and React keying are all `${kind}:${id}`, so two features sharing an id
 * are one object as far as the editor is concerned: clicking either selects both, and moving
 * one moves both. That is not hypothetical — fifteen stored floorplans give *every* floor
 * feature the zero UUID for both `id` and `featureId`, which turned all of a plan's desk
 * shapes into a single un-editable blob. Two plans do the same to their annotations and one
 * to its desks.
 *
 * Only the editor's own handle is replaced, and only when the stored one cannot do the job.
 * `featureId` is never touched: for a desk it is the `Workplaces.Id` that makes it bookable,
 * so inventing one would break the booking link it names.
 *
 * The fallback is derived from the feature's position rather than randomly generated, so the
 * same stored plan yields the same ids on every load and an id cannot change under a
 * selection that is already held.
 */
const uniqueIdentifiers = (prefix: string): ((rawId: unknown, index: number) => string) => {
  const seen = new Set<string>();

  return (rawId: unknown, index: number): string => {
    let candidate = isUsableIdentifier(rawId) ? rawId : `${prefix}-${index}`;

    // A stored id could itself collide with the positional fallback, so keep counting rather
    // than trusting one attempt. Bounded: every suffix tried is new.
    let attempt = 0;
    while (seen.has(candidate)) {
      attempt += 1;
      candidate = attempt === 1 ? `${prefix}-${index}` : `${prefix}-${index}-${attempt}`;
    }

    seen.add(candidate);
    return candidate;
  };
};

const unknownPropertiesOf = (
  properties: Record<string, unknown> | undefined,
  knownKeys: string[],
): Record<string, unknown> => {
  if (!properties) {
    return {};
  }

  return Object.fromEntries(Object.entries(properties).filter(([key]) => !knownKeys.includes(key)));
};

/**
 * A vertex as `[x, y]`, dropping anything after the second number.
 *
 * GeoJSON allows a third element for elevation, and the drawing tools these plans came from
 * emit it inconsistently: nine stored floorplans carry `[x, y, z]` polygon vertices, seven of
 * them for every vertex they have. Requiring exactly two silently discarded those features —
 * a whole ring failing one vertex takes the ring, its polygon and the feature with it — which
 * left the affected plans drawn as desks floating on an empty sheet, with no walls, rooms or
 * outline at all. Nothing here uses elevation, so the extra element is dropped rather than
 * being allowed to delete a building.
 */
const asCoordinate = (value: unknown): Coordinate | undefined => {
  if (!Array.isArray(value) || value.length < 2) {
    return undefined;
  }

  const [x, y] = value;

  if (typeof x !== 'number' || !Number.isFinite(x) || typeof y !== 'number' || !Number.isFinite(y)) {
    return undefined;
  }

  return [x, y];
};

const asRing = (ring: unknown): Coordinate[] | undefined => {
  if (!Array.isArray(ring)) {
    return undefined;
  }

  const vertices = ring.map(asCoordinate);
  return vertices.every((vertex): vertex is Coordinate => vertex !== undefined) ? vertices : undefined;
};

const asPolygon = (polygon: unknown): Coordinate[][] | undefined => {
  if (!Array.isArray(polygon)) {
    return undefined;
  }

  const rings = polygon.map(asRing);
  return rings.every((ring): ring is Coordinate[] => ring !== undefined) ? rings : undefined;
};

/**
 * Reads MultiPolygon coordinates, and only MultiPolygon coordinates.
 *
 * The stored data is not uniform: one floor feature is a MultiLineString and 23 room
 * features are plain Polygons. Reading either at a MultiPolygon's nesting depth yields
 * NaN geometry rather than an error, so the shape is checked instead of assumed.
 */
const asMultiPolygon = (coordinates: unknown): MultiPolygonCoordinates | undefined => {
  if (!Array.isArray(coordinates)) {
    return undefined;
  }

  const polygons = coordinates.map(asPolygon);
  return polygons.every((polygon): polygon is Coordinate[][] => polygon !== undefined) ? polygons : undefined;
};

/** A Polygon promoted to a single-polygon MultiPolygon, so the renderer has one shape to handle. */
const asPromotedPolygon = (coordinates: unknown): MultiPolygonCoordinates | undefined => {
  const polygon = asPolygon(coordinates);
  return polygon ? [polygon] : undefined;
};

/**
 * The runs of points of a line feature, whether it is a single line or many.
 *
 * Not decoration: BDO Maastricht's ground floor keeps its whole floor drawing in one
 * `MultiLineString`, so skipping these leaves that plan with rooms and an outline and nothing
 * between them.
 */
const linePathsOf = (feature: RawFeature): Coordinate[][] | undefined => {
  if (feature.geometry?.type === 'MultiLineString') {
    const paths = Array.isArray(feature.geometry.coordinates)
      ? feature.geometry.coordinates.map(asRing)
      : [];

    const usable = paths.filter((path): path is Coordinate[] => path !== undefined && path.length > 1);
    return usable.length > 0 ? usable : undefined;
  }

  if (feature.geometry?.type === 'LineString') {
    const path = asRing(feature.geometry.coordinates);
    return path && path.length > 1 ? [path] : undefined;
  }

  return undefined;
};

const lineFeaturesOf = (features: RawFeature[]): LineBackdropFeature[] => features
  .map((feature, index) => {
    const paths = linePathsOf(feature);
    return paths ? { id: `floor-line-${asString(feature.properties?.id, String(index))}-${index}`, paths } : undefined;
  })
  .filter((feature): feature is LineBackdropFeature => feature !== undefined);

const backdropCoordinatesOf = (feature: RawFeature): MultiPolygonCoordinates | undefined => {
  if (feature.geometry?.type === 'MultiPolygon') {
    return asMultiPolygon(feature.geometry.coordinates);
  }

  if (feature.geometry?.type === 'Polygon') {
    return asPromotedPolygon(feature.geometry.coordinates);
  }

  // Anything else — a line, a feature with no geometry at all — is not a filled shape. Lines
  // are read separately by `lineFeaturesOf`; the rest is skipped rather than drawn wrong. All
  // of it stays in the stored layer either way: the editor never submits the backdrop.
  return undefined;
};

const KNOWN_POLYGON_PROPERTIES = ['id', 'featureId', 'elementType', 'rotation', 'centreline', 'thickness'];

const asStructuralType = (value: unknown): StructuralType | null => (
  typeof value === 'string' && (STRUCTURAL_TYPES as readonly string[]).includes(value)
    ? (value as StructuralType)
    : null
);

/**
 * The centreline a structural element was generated from, when it carries one.
 *
 * Only ever written by this editor, so an absent or malformed value is the normal case
 * rather than a fault — every stored wall predates the property, and one imported from CAD
 * has no centreline to record.
 */
const asCentreline = (value: unknown): readonly [Coordinate, Coordinate] | null => {
  if (!Array.isArray(value) || value.length !== 2) {
    return null;
  }

  const from = asCoordinate(value[0]);
  const to = asCoordinate(value[1]);

  return from && to ? [from, to] : null;
};

const polygonFeaturesOf = (
  features: RawFeature[],
  layer: PolygonLayer,
  idPrefix: string,
): PolygonFeature[] => {
  const identify = uniqueIdentifiers(idPrefix);

  return features
    .map((feature, index) => {
      const coordinates = backdropCoordinatesOf(feature);

      if (!coordinates) {
        return undefined;
      }

      const properties = feature.properties ?? {};
      const centreline = asCentreline(properties.centreline);
      const elementType = layer === 'floor' ? asStructuralType(properties.elementType) : null;
      // A floor type this editor does not know is kept as it was found and saved back as it was:
      // it is still the feature's, and the server takes any floor type. Dropping it would retype
      // the feature on the next save.
      const unknownType = layer === 'floor' && !elementType && typeof properties.elementType === 'string'
        ? { elementType: properties.elementType }
        : {};

      const polygon: PolygonFeature = {
        kind: 'polygon',
        layer,
        // Prefixed per layer as well as per feature: a room and a floor polygon regularly
        // share an id in the stored data, and the editor keys selection on `${kind}:${id}`.
        id: `${idPrefix}-${identify(properties.id, index)}`,
        storedId: typeof properties.id === 'string' ? properties.id : `${idPrefix}-${index}`,
        featureId: asString(properties.featureId, `${idPrefix}-${index}`),
        geometryType: feature.geometry?.type === 'Polygon' ? 'Polygon' : 'MultiPolygon',
        coordinates,
        rotation: asNumber(properties.rotation, 0),
        elementType,
        centreline,
        thickness: centreline ? asNumber(properties.thickness, 0) || null : null,
        unknownProperties: { ...unknownPropertiesOf(properties, KNOWN_POLYGON_PROPERTIES), ...unknownType },
      };

      return polygon;
    })
    .filter((feature): feature is PolygonFeature => feature !== undefined);
};

const isDeskShape = (feature: RawFeature): boolean => feature.properties?.elementType === DESK_ELEMENT_TYPE;

const normalizeDesks = (layer: unknown): DeskFeature[] => {
  const identify = uniqueIdentifiers('desk');

  return featuresOf(layer)
    .map((feature, index) => {
      const coordinates = feature.geometry?.type === 'Point' ? asCoordinate(feature.geometry.coordinates) : undefined;

      if (!coordinates) {
        return undefined;
      }

      const properties = feature.properties ?? {};
      const deskType: DeskType = properties.deskType === 'pod' ? 'pod' : 'single';

      const desk: DeskFeature = {
        kind: 'desk',
        id: identify(properties.id, index),
        storedId: typeof properties.id === 'string' ? properties.id : `desk-${index}`,
        // Left exactly as stored, however unusable it looks: this is the `Workplaces.Id`
        // that makes the desk bookable, and inventing one would name a row that isn't there.
        featureId: asString(properties.featureId, `desk-${index}`),
        coordinates,
        rotation: asNumber(properties.rotation, 0),
        deskType,
        podId: deskType === 'pod' && typeof properties.podId === 'string' ? properties.podId : null,
        // Stored without the `mdi-` prefix, which is what the backend normalises annotations
        // to and what the icon set is keyed on.
        iconName: typeof properties.iconName === 'string' && properties.iconName
          ? properties.iconName.replace(/^mdi-/, '')
          : null,
        capacity: asNumber(properties.capacity, 1),
        // Left absent unless a real, positive size was stored, so a plan saved before this
        // existed loads exactly as it always did.
        ...(typeof properties.iconScale === 'number' && properties.iconScale > 0
          ? { iconScale: properties.iconScale }
          : {}),
        // Only ever an echo of the Workplaces table. No floorplan stored before the editor
        // existed carries it, and `applyRoomAssignments` overwrites it with what the database
        // actually says as soon as that has been fetched.
        roomId: typeof properties.roomId === 'string' && properties.roomId ? properties.roomId : null,
        unknownProperties: unknownPropertiesOf(properties, KNOWN_DESK_PROPERTIES),
      };

      return desk;
    })
    .filter((desk): desk is DeskFeature => desk !== undefined);
};

const normalizeDeskShapes = (layer: unknown): DeskShapeFeature[] => {
  const identify = uniqueIdentifiers('desk-shape');

  return featuresOf(layer)
    .map((feature, index) => {
      const coordinates = feature.geometry?.type === 'MultiPolygon'
        ? asMultiPolygon(feature.geometry.coordinates)
        : undefined;

      if (!isDeskShape(feature) || !coordinates) {
        return undefined;
      }

      const properties = feature.properties ?? {};
      const shape: DeskShapeFeature = {
        kind: 'deskShape',
        id: identify(properties.id, index),
        storedId: typeof properties.id === 'string' ? properties.id : `desk-shape-${index}`,
        featureId: asString(properties.featureId, `desk-shape-${index}`),
        coordinates,
        rotation: asNumber(properties.rotation, 0),
        iconName: typeof properties.iconName === 'string' && properties.iconName
          ? properties.iconName.replace(/^mdi-/, '')
          : null,
        unknownProperties: unknownPropertiesOf(properties, KNOWN_DESK_SHAPE_PROPERTIES),
      };

      return shape;
    })
    .filter((shape): shape is DeskShapeFeature => shape !== undefined);
};

type AnnotationFeature = TextLabelFeature | IconLabelFeature;

const normalizeLabel = (
  feature: RawFeature,
  index: number,
  identify: (rawId: unknown, index: number) => string,
): AnnotationFeature | undefined => {
  const coordinates = feature.geometry?.type === 'Point' ? asCoordinate(feature.geometry.coordinates) : undefined;

  if (!coordinates) {
    return undefined;
  }

  const properties = feature.properties ?? {};
  const rawIconName = typeof properties.iconName === 'string' && properties.iconName ? properties.iconName : null;

  // Every annotation stored today predates `kind` and carries its content under `label`.
  // Missing has always implicitly meant text, so it is inferred rather than rejected; an
  // explicit kind wins, and a feature with only an icon name is treated as an icon.
  const text = asString(properties.text, asString(properties.label, ''));
  const isIcon = properties.kind === 'icon' || (properties.kind === undefined && !text && rawIconName !== null);

  const shared = {
    id: identify(properties.id, index),
    storedId: typeof properties.id === 'string' ? properties.id : `label-${index}`,
    featureId: asString(properties.featureId, `label-${index}`),
    coordinates,
    rotation: asNumber(properties.rotation, 0),
    color: asString(properties.color, DEFAULT_LABEL_COLOR),
    unknownProperties: unknownPropertiesOf(properties, KNOWN_LABEL_PROPERTIES),
  };

  if (isIcon) {
    return {
      ...shared,
      kind: 'iconLabel',
      iconName: rawIconName === null ? null : rawIconName.replace(/^mdi-/, ''),
      iconSize: asNumber(properties.iconSize, DEFAULT_ICON_SIZE),
    };
  }

  return { ...shared, kind: 'textLabel', text, fontSize: asNumber(properties.fontSize, DEFAULT_FONT_SIZE) };
};

const normalizeLabels = (layer: unknown): AnnotationFeature[] => {
  const identify = uniqueIdentifiers('label');

  return featuresOf(layer)
    .map((feature, index) => normalizeLabel(feature, index, identify))
    .filter((label): label is AnnotationFeature => label !== undefined);
};

export const normalizeFloorPlan = (floorPlan: StoredFloorPlan): EditableFloorPlan => {
  const layerEnvelopes = {
    border: envelopeOf(floorPlan.borderLayer),
    floor: envelopeOf(floorPlan.floorLayer),
    room: envelopeOf(floorPlan.roomLayer),
    workplaces: envelopeOf(floorPlan.workplacesLayer),
    label: envelopeOf(floorPlan.labelLayer),
  } as Record<LayerName, Record<string, unknown>>;

  return {
    floorPlanId: floorPlan.id ?? '',
    officeId: floorPlan.officeId ?? '',
    storey: floorPlan.storey ?? 0,
    lastUpdatedAt: floorPlan.lastUpdatedAt,
    publishedAt: floorPlan.publishedAt,
    unitsPerMetre: typeof floorPlan.unitsPerMetre === 'number' && floorPlan.unitsPerMetre > 0
      ? floorPlan.unitsPerMetre
      : null,
    border: polygonFeaturesOf(featuresOf(floorPlan.borderLayer), 'border', 'border'),
    rooms: polygonFeaturesOf(featuresOf(floorPlan.roomLayer), 'room', 'room'),
    floorBackdrop: polygonFeaturesOf(
      featuresOf(floorPlan.floorLayer).filter((feature) => !isDeskShape(feature)),
      'floor',
      'floor',
    ),
    floorLines: lineFeaturesOf(featuresOf(floorPlan.floorLayer).filter((feature) => !isDeskShape(feature))),
    desks: normalizeDesks(floorPlan.workplacesLayer),
    deskShapes: normalizeDeskShapes(floorPlan.floorLayer),
    labels: normalizeLabels(floorPlan.labelLayer),
    layerEnvelopes,
  };
};

export default normalizeFloorPlan;
